/**
 * useTheme — applies the admin's appearance preferences to the live DOM by
 * writing CSS variables and toggling utility classes on <html>.
 *
 * Mount it once high in the tree (e.g. inside <Layout/>). It returns the
 * resolved (concrete) theme so consumers can mirror it in component-level
 * decisions without re-implementing the system-preference detection.
 */
import { useEffect, useMemo, useState } from 'react';
import { useAdminSettings } from '@/src/modules/store/admin-settings.context';

function resolveSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: light)').matches
    ? 'light'
    : 'dark';
}

export function useTheme() {
  const { settings } = useAdminSettings();
  const { appearance, accessibility } = settings;
  const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>(resolveSystemTheme);

  useEffect(() => {
    if (appearance.themeMode !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const handler = () => setSystemTheme(mq.matches ? 'light' : 'dark');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [appearance.themeMode]);

  const resolved: 'light' | 'dark' =
    appearance.themeMode === 'system' ? systemTheme : appearance.themeMode;

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = resolved;
    root.classList.toggle('theme-light', resolved === 'light');
    root.classList.toggle('theme-dark', resolved === 'dark');

    const style = root.style;
    style.setProperty('--color-primary', appearance.primaryColor);
    style.setProperty('--color-secondary-container', appearance.secondaryColor);
    style.setProperty('--color-tertiary-container', appearance.accentColor);
    style.setProperty('--admin-radius', `${appearance.borderRadius}px`);
    style.setProperty('--admin-font-size', `${appearance.fontSize}px`);
    style.setProperty('--admin-font-family', `"${appearance.fontFamily}"`);

    root.classList.toggle('compact-mode', appearance.compactMode);
    root.classList.toggle('no-animations', !appearance.animationsEnabled || accessibility.reducedMotion);
    root.classList.toggle('high-contrast', accessibility.highContrast);
    root.classList.toggle('larger-text', accessibility.largerText);
    root.classList.toggle('focus-visible-strong', accessibility.focusIndicators);
  }, [
    resolved,
    appearance.primaryColor,
    appearance.secondaryColor,
    appearance.accentColor,
    appearance.borderRadius,
    appearance.fontSize,
    appearance.fontFamily,
    appearance.compactMode,
    appearance.animationsEnabled,
    accessibility.reducedMotion,
    accessibility.highContrast,
    accessibility.largerText,
    accessibility.focusIndicators,
  ]);

  return useMemo(
    () => ({ resolvedTheme: resolved, mode: appearance.themeMode }),
    [resolved, appearance.themeMode],
  );
}
